import React from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Button,
  Tooltip,
  useTheme,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import DeleteIcon from "@mui/icons-material/Delete";
import FeedbackIcon from "@mui/icons-material/Feedback";
import SettingsIcon from "@mui/icons-material/Settings";
import InfoIcon from "@mui/icons-material/Info";
import { keyframes } from "@mui/material/styles";

import {
  WorkflowStepData,
  StepStatus,
  DeleteStepPayload,
} from "../../../types/process";

interface WorkflowStepItemProps {
  step: WorkflowStepData;
  categoryId: string;
  processId: string;
  onStepClick: () => void;
  onFeedbackClick: () => void;
  onDeleteClick: (payload: DeleteStepPayload) => void;
}

const WorkflowStepItem: React.FC<WorkflowStepItemProps> = ({
  step,
  categoryId,
  processId,
  onStepClick,
  onFeedbackClick,
  onDeleteClick,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const pulse = keyframes`
    0% {
      transform: scale(1);
      opacity: 1;
    }
    50% {
      transform: scale(1.1);
      opacity: 0.7;
    }
    100% {
      transform: scale(1);
      opacity: 1;
    }
  `;

  const getStatusIcon = () => {
    if (step.status === StepStatus.COMPLETE) {
      return (
        <CheckCircleIcon
          sx={{
            color: theme.palette.success.main,
            fontSize: 28,
            filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.2))",
          }}
        />
      );
    }
    if (step.requiresConfiguration) {
      return (
        <HelpOutlineIcon
          sx={{
            color: theme.palette.warning.main,
            fontSize: 28,
            animation: `${pulse} 2s ease-in-out infinite`,
          }}
        />
      );
    }
    return (
      <ErrorOutlineIcon
        sx={{
          color: theme.palette.error.main,
          fontSize: 28,
        }}
      />
    );
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (window.confirm(t("confirm_delete_step", { stepName: step.name }))) {
      onDeleteClick({ categoryId, processId, stepId: step.id });
    }
  };

  const handleFeedbackButtonClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onFeedbackClick();
  };

  const getSelectedAppName = () => {
    if (step.currentConfig?.selectedAppId && step.config?.availableApps) {
      const app = step.config.availableApps.find(
        app => app.id === step.currentConfig?.selectedAppId,
      );
      return app?.name || "N/A";
    }
    return null;
  };

  const selectedAppName = getSelectedAppName();

  return (
    <Paper
      sx={{
        p: { xs: 2, sm: 2.5 },
        cursor: "pointer",
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        background: "#ffffff",
        borderRadius: 2,
        border: "1px solid",
        borderColor: "rgba(0,0,0,0.08)",
        "&:hover": {
          boxShadow: "0 6px 20px rgba(0,0,0,0.1)",
          transform: { xs: "none", sm: "translateY(-3px)" },
          borderColor: "rgba(37, 117, 252, 0.3)",
        },
      }}
      onClick={onStepClick}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "stretch", sm: "center" },
          justifyContent: "space-between",
          gap: { xs: 2, sm: 0 },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: { xs: 1.5, sm: 2 },
            flex: 1,
            minWidth: 0,
          }}
        >
          <Tooltip
            title={
              step.status === StepStatus.COMPLETE
                ? t("step_complete")
                : step.requiresConfiguration
                  ? t("step_requires_configuration")
                  : t("step_incomplete")
            }
          >
            <Box>{getStatusIcon()}</Box>
          </Tooltip>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography
                variant="body1"
                sx={{
                  fontWeight: 600,
                  color: "#1a1a1a",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  display: "block",
                }}
                title={step.name}
              >
                {step.name}
              </Typography>
              {step.hasFeedback && (
                <Tooltip title={t("feedback_provided")}>
                  <FeedbackIcon
                    sx={{ fontSize: 16, color: theme.palette.info.main }}
                  />
                </Tooltip>
              )}
            </Box>
            {step.status === StepStatus.COMPLETE && selectedAppName && (
              <Typography
                variant="caption"
                sx={{
                  color: "#666",
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  mt: 0.5,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
                title={`${t("using")}: ${selectedAppName}`}
              >
                <CheckCircleIcon
                  sx={{
                    fontSize: 14,
                    color: theme.palette.success.main,
                    flexShrink: 0,
                  }}
                />
                {t("using")}: {selectedAppName}
              </Typography>
            )}
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: { xs: 0.5, sm: 1 },
            flexWrap: { xs: "wrap", sm: "nowrap" },
            justifyContent: { xs: "flex-end", sm: "flex-start" },
            width: { xs: "100%", sm: "auto" },
          }}
        >
          <Button
            size="small"
            variant={
              step.status === StepStatus.INCOMPLETE ? "contained" : "outlined"
            }
            startIcon={
              <Box sx={{ display: { xs: "none", sm: "inline-flex" } }}>
                {step.status === StepStatus.INCOMPLETE ? (
                  <SettingsIcon />
                ) : (
                  <InfoIcon />
                )}
              </Box>
            }
            onClick={e => {
              e.stopPropagation();
              onStepClick();
            }}
            sx={{
              borderRadius: 2,
              textTransform: "none",
              px: { xs: 1.5, sm: 2 },
              minWidth: { xs: "auto", sm: "64px" },
              fontSize: { xs: "0.75rem", sm: "0.875rem" },
              ...(step.status === StepStatus.INCOMPLETE
                ? {
                    background:
                      "linear-gradient(135deg, #6a11cb 0%, #2575fc 100%)",
                    "&:hover": {
                      background:
                        "linear-gradient(135deg, #5a01bb 0%, #1565ec 100%)",
                    },
                  }
                : {
                    color: "#2575fc",
                    borderColor: "#2575fc",
                    "&:hover": {
                      backgroundColor: "rgba(37, 117, 252, 0.08)",
                      borderColor: "#2575fc",
                    },
                  }),
            }}
          >
            {step.status === StepStatus.INCOMPLETE
              ? t("configure")
              : t("details")}
          </Button>
          <Button
            size="small"
            variant="text"
            startIcon={
              <Box sx={{ display: { xs: "none", sm: "inline-flex" } }}>
                <FeedbackIcon />
              </Box>
            }
            onClick={handleFeedbackButtonClick}
            sx={{
              color: "#764ba2",
              borderRadius: 2,
              textTransform: "none",
              px: { xs: 1.5, sm: 2 },
              minWidth: { xs: "auto", sm: "64px" },
              fontSize: { xs: "0.75rem", sm: "0.875rem" },
              "&:hover": {
                backgroundColor: "rgba(118, 75, 162, 0.08)",
              },
            }}
          >
            {t("feedback.")}
          </Button>
          <Tooltip title={t("delete")}>
            <IconButton
              size="small"
              color="error"
              onClick={handleDelete}
              sx={{
                "&:hover": {
                  backgroundColor: theme.palette.error.light,
                  color: theme.palette.error.contrastText,
                },
              }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
    </Paper>
  );
};

export default WorkflowStepItem;
